import fs from 'node:fs';
import path from 'node:path';
// `ignore` is CJS (`module.exports = factory`); under NodeNext the default
// import is the factory at runtime, but its .d.ts types it as a namespace —
// hence the cast.
import ignorePkg, { type Ignore } from 'ignore';
const ignore = ignorePkg as unknown as (options?: { ignorecase?: boolean }) => Ignore;
import {
  MANIFEST_FILE_CAP_BYTES,
  MANIFEST_TOTAL_CAP_BYTES,
} from '../constants.js';
import { isUtf8Text } from './substitute.js';
import { normalizeSecrets, type AppConfig } from './appConfig.js';

export interface ManifestFile {
  path: string;
  content: string;
}

/** Matches admin-app's ManifestSchema (blocks/types.ts). */
export interface AppManifest {
  slug: string;
  name: string;
  description: string;
  database: boolean;
  /** Private per-app file storage (defaults server-side to false). */
  fileStorage?: boolean;
  /** Who can reach the deployed app (defaults server-side to 'public'). */
  accessMode?: 'public' | 'invite' | 'members' | 'byo';
  secrets: Array<{ name: string }>;
  env: Record<string, string>;
  files: ManifestFile[];
}

export interface CollectResult {
  files: ManifestFile[];
  /** Non-UTF-8 files that cannot ride in a JSON manifest; skipped. */
  skippedBinary: string[];
  totalBytes: number;
}

/** Always excluded regardless of .gitignore. */
const ALWAYS_IGNORE = ['.git/', 'node_modules/', 'dist/', '.env*'];

/**
 * Collect the scaffolded working tree into manifest files. Respects the
 * root .gitignore plus hard exclusions; enforces per-file and total caps.
 * Paths are posix-style relative paths (the repo.scaffold contract).
 */
export function collectFiles(rootDir: string): CollectResult {
  const ig = ignore().add(ALWAYS_IGNORE);
  const gitignorePath = path.join(rootDir, '.gitignore');
  if (fs.existsSync(gitignorePath)) {
    ig.add(fs.readFileSync(gitignorePath, 'utf8'));
  }

  const files: ManifestFile[] = [];
  const skippedBinary: string[] = [];
  let totalBytes = 0;

  const walk = (dir: string, relBase: string): void => {
    for (const entry of fs.readdirSync(dir, { withFileTypes: true })) {
      const rel = relBase ? `${relBase}/${entry.name}` : entry.name;
      if (entry.isDirectory()) {
        if (ig.ignores(`${rel}/`)) continue;
        walk(path.join(dir, entry.name), rel);
      } else if (entry.isFile()) {
        if (ig.ignores(rel)) continue;
        const buf = fs.readFileSync(path.join(dir, entry.name));
        if (buf.byteLength > MANIFEST_FILE_CAP_BYTES) {
          throw new Error(
            `${rel} is ${Math.round(buf.byteLength / 1024)}KB — files over ` +
              `${MANIFEST_FILE_CAP_BYTES / 1024}KB can't be provisioned. ` +
              'Add it to .gitignore or remove it.',
          );
        }
        if (!isUtf8Text(buf)) {
          skippedBinary.push(rel);
          continue;
        }
        totalBytes += buf.byteLength;
        if (totalBytes > MANIFEST_TOTAL_CAP_BYTES) {
          throw new Error(
            `The app tree exceeds the ${Math.round(MANIFEST_TOTAL_CAP_BYTES / 1024 / 1024)}MB ` +
              'provision limit. Add build artifacts and data files to .gitignore.',
          );
        }
        files.push({ path: rel, content: buf.toString('utf8') });
      }
    }
  };

  walk(rootDir, '');
  files.sort((a, b) => a.path.localeCompare(b.path));
  return { files, skippedBinary, totalBytes };
}

/** Assemble the POST /blocks/provision manifest from app.config.json + tree. */
export function assembleManifest(config: AppConfig, rootDir: string): {
  manifest: AppManifest;
  skippedBinary: string[];
} {
  const { files, skippedBinary } = collectFiles(rootDir);
  return {
    manifest: {
      slug: config.slug,
      name: config.name,
      description: config.description ?? '',
      database: config.database === true,
      fileStorage: config.fileStorage === true,
      ...(config.accessMode ? { accessMode: config.accessMode } : {}),
      secrets: normalizeSecrets(config.secrets),
      env: config.env && typeof config.env === 'object' ? config.env : {},
      files,
    },
    skippedBinary,
  };
}
